<template>
  <div>
    <b-modal
      v-model="isOpen"
      size="xl"
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      modal-class="import-booking-modal"
      header-class="import-booking-modal-header"
      body-class="import-booking-modal-body"
      @show="loadLeads"
    >
      <template #modal-header>
        <div class="w-100">
          <div class="d-flex align-items-start">
            <h4 class="mb-0 flex-grow-1">Importar reserva</h4>
            <button
              type="button"
              class="close import-booking-modal-close"
              aria-label="Close"
              @click.prevent="closeModal"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.5 1.5L16.5 16.5"
                  stroke="#8D8D8D"
                  stroke-width="3"
                  stroke-linecap="round"
                />
                <path
                  d="M16.5 1.5L1.5 16.5"
                  stroke="#8D8D8D"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <div class="import-booking-modal-search mt-3 w-100">
            <base-input
              v-model="searchQuery"
              placeholder="Buscar reserva..."
            ></base-input>
          </div>
        </div>
      </template>

      <modal-loading
        v-if="isLoading"
        :openModal="isLoading"
        :value="99"
        style="width: 10em"
        :label="'Carregando reservas...'"
      >
      </modal-loading>

      <div v-else class="import-booking-list">
        <div
          v-if="filteredLeads.length === 0"
          class="import-booking-empty"
        >
          Nenhuma reserva disponível.
        </div>

        <button
          v-for="lead in filteredLeads"
          :key="lead.hash"
          type="button"
          class="import-booking-card"
          :class="{ 'import-booking-card--selected': selectedHash === lead.hash }"
          @click="selectLead(lead)"
        >
          <div class="import-booking-card-top">
            <span class="import-booking-code">#{{ lead.booking_code }}</span>
            <b-badge :variant="statusVariant(lead.status)" class="ml-2">
              {{ lead.status }}
            </b-badge>
            <strong class="ml-auto">
              {{ formatCurrency(getLeadTotal(lead)) }}
            </strong>
          </div>

          <div class="import-booking-title">
            {{ lead.items && lead.items[0] ? lead.items[0].title : 'Passeio' }}
          </div>

          <div class="import-booking-meta">
            <span class="text-muted">Local:</span> {{ lead.product_place }}
            <span v-if="lead.date">
              · <span class="text-muted">Data:</span>
              {{ formatDate(lead.date) }}
            </span>
            <span v-if="lead.time">
              · <span class="text-muted">Horário:</span> {{ lead.time }}
            </span>
            <span v-if="lead.people">
              · <span class="text-muted">Pessoas:</span> {{ lead.people }}
            </span>
          </div>

          <!-- <div class="import-booking-meta">
            <span class="text-muted">Canal:</span> {{ lead.channel }}
            <span v-if="lead.created_date">
              · <span class="text-muted">Criada em:</span>
              {{ lead.created_date }}
            </span>
          </div> -->

          <div v-if="lead.observation" class="import-booking-meta">
            <span class="text-muted">Obs:</span> {{ lead.observation }}
          </div>

          <ul
            v-if="lead.items && lead.items.length"
            class="import-booking-items mb-0 pl-3"
          >
            <li v-for="(item, idx) in lead.items" :key="idx">
              {{ item.quantity }}x {{ item.label }} —
              {{ formatCurrency(item.quantity * item.unit_price) }}
            </li>
          </ul>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import Fuse from 'fuse.js'
import nodeService from '@/services/nodejs'
import ModalLoading from '../../components/ModalLoading/ModalLoading.vue'

export default {
  name: 'ImportBookingModal',

  components: {
    ModalLoading,
  },

  props: {
    isImportBookingModalOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      selectedHash: null,
      leads: [],
      searchQuery: '',
      fuseSearch: null,
    }
  },

  computed: {
    isOpen: {
      get() {
        return this.isImportBookingModalOpen
      },
      set(value) {
        if (!value) this.closeModal()
      },
    },

    filteredLeads() {
      const query = String(this.searchQuery || '').trim()
      if (!query || !this.fuseSearch) return this.leads
      return this.fuseSearch.search(query).map((result) => result.item)
    },
  },

  methods: {
    async loadLeads() {
      this.isLoading = true
      this.selectedHash = null
      this.searchQuery = ''

      try {
        const response = await nodeService.getLeads()
        this.leads = (response.data || []).map((lead) => ({
          ...lead,
          searchText: [
            lead.booking_code,
            lead.product_place,
            lead.status,
            lead.channel,
            lead.hash,
            lead.observation,
            lead.date,
            lead.time,
            lead.people,
            ...(lead.items || []).map((item) => `${item.title} ${item.label}`),
          ].join(' '),
        }))
        this.fuseSearch = new Fuse(this.leads, {
          keys: [
            'booking_code',
            'product_place',
            'status',
            'channel',
            'hash',
            'observation',
            'date',
            'time',
            'people',
            'searchText',
          ],
          threshold: 0.3,
        })
      } catch (error) {
        console.error('Erro ao carregar reservas:', error)
        this.$notify({
          message: 'Erro ao carregar reservas disponíveis.',
          duration: 3000,
          type: 'error',
        })
        this.leads = []
        this.fuseSearch = null
      } finally {
        this.isLoading = false
      }
    },

    selectLead(lead) {
      this.selectedHash = lead.hash
      this.$emit('importBooking', lead)
      this.closeModal()
    },

    closeModal() {
      this.searchQuery = ''
      this.selectedHash = null
      this.$emit('closeImportBookingModal')
    },

    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : ''
    },

    formatCurrency(value) {
      return Number(value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },

    getLeadTotal(lead) {
      if (!lead.items || !lead.items.length) return 0
      return lead.items.reduce((total, item) => {
        const price = Number(item.unit_price) - Number(item.unit_discount || 0)
        return total + Number(item.quantity) * price
      }, 0)
    },

    statusVariant(status) {
      const variants = {
        PENDING: 'warning',
        CONFIRMED: 'success',
        CANCELLED: 'danger',
      }
      return variants[status] || 'secondary'
    },
  },
}
</script>

<style scoped>

.import-booking-modal-search {
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.import-booking-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.import-booking-empty {
  padding: 24px 12px;
  color: #8a8a8a;
  font-size: 14px;
  text-align: center;
}

.import-booking-card {
  display: block;
  width: 100%;
  padding: 18px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.import-booking-card:hover {
  border-color: #12576e;
}

.import-booking-card--selected {
  border-color: #28a745;
}

.import-booking-card-top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.import-booking-code {
  color: #12576e;
  font-weight: 700;
}

.import-booking-title {
  margin-top: 10px;
  margin-bottom: 10px;
  font-weight: 600;
}

.import-booking-meta {
  margin-top: 2px;
  font-size: 14px;
  color: #6c6c6c;
}

.import-booking-items {
  margin-top: 10px;
  font-size: 13px;
  color: #3d3d3d;
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px 16px;
}

@media (min-width: 768px) {
  .import-booking-items {
    grid-template-columns: 1fr 1fr;
  }
}
</style>

<style>
.import-booking-modal .modal-content {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.import-booking-modal .import-booking-modal-header {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #ececec;
}

.import-booking-modal .import-booking-modal-body {
  overflow-y: auto;
  flex: 1 1 auto;
  max-height: calc(90vh - 120px);
}
</style>
